import {
  FolderManagerAction,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerItemTypes,
  FolderManagerMimeTypes,
  FolderManagerStatusTypes,
} from '../interfaces';
import ImageIcon from '@mui/icons-material/Image';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import GridViewIcon from '@mui/icons-material/GridView';
import styled from 'styled-components';
import classNames from 'classnames';

import { CircularLoader } from '@clatter/ui';
import { formatDateTime } from '@clatter/platform';
import { formatFileSize } from './files';
import folderIcon from '../../assets/icons/folder-blue.svg';
import sharedFolderIcon from '../../assets/icons/shared-folder-blue.svg';
import { FolderManagerItemActions } from '../components/FolderManager/Components/FolderManagerItemActions';
import { ShortcutIcon } from '../components/FolderManager/Components/ShortcutIcon';
interface Props {
  $mimeType: string;
}

const StyledFolderIconWrapper = styled.div`
  position: relative;
  display: inline-flex;

  .slide-repo-badge {
    position: absolute;
    left: 2px;
    top: 55%;
    transform: translateY(-50%);
    font-size: 16px;
    color: #fff !important;
    pointer-events: none;
  }
`;

interface ColumnOptions {
  selectedItems: any;
  slug: string | undefined;
  onAction: ({ actionType, payload }: FolderManagerAction) => void;
  isLoading: boolean;
}

const StyledNameCell = styled.div<{ type: string }>`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;

  &.orphaned {
    opacity: 0.5;
  }
`;

export const mapSlideToFolderManagerItem = ({
  item,
  actions,
}: {
  item: PresentationSlideFromApi;
  actions: IFolderManagerActionsItem[];
}): Partial<FolderManagerItem<PresentationSlideFromApi>> => {
  return {
    id: `${item.AssetVersionID}-${item.SlideNumber}`,
    displayName: item.Title,
    type: FolderManagerMimeTypes.SLIDE,
    thumbnailUrl: item.ThumbnailLocation,
    itemData: item,
    actions: actions,
  };
};

export const mapAssetToFolderManager = (asset: AssetFromApi): Partial<FolderManagerItem> => {
  return {
    id: asset.ID,
    name: asset?.DisplayName,
    type: asset?.Type,
    thumbnailUrl: asset?.CurrentVersion?.ThumbnailUrl || null,
    parentId: asset.FolderID,
    fileSize: asset?.CurrentVersion?.FileSize,
    owner: asset?.Owner,
    createdBy: asset?.CreatedBy,
    slides: (asset?.Slides || []).map((item) =>
      mapSlideToFolderManagerItem({
        item,
        actions: [
          {
            name: FolderManagerItemActionNames.ADD_TO_PRESENTATION,
            type: FolderManagerItemActionTypes.MULTI,
          },
        ],
      }),
    ) as FolderManagerItem[],

    currentVersion: asset.CurrentVersion,
    trashedAt: asset?.TrashedAt,
    createdAt: asset?.CreatedAt,
    updatedAt: asset?.UpdatedAt,
    actions: getActionsForItemType(asset?.Type as FolderManagerItemTypes),
  };
};

export const StyledIconWrapper = styled.div<Props>`
  min-width: ${({ $mimeType }) =>
    $mimeType === FolderManagerMimeTypes.FOLDER || $mimeType === FolderManagerMimeTypes.WORKSPACE ? '25px' : 'auto'};
  min-height: ${({ $mimeType }) =>
    $mimeType === FolderManagerMimeTypes.FOLDER || $mimeType === FolderManagerMimeTypes.WORKSPACE ? '25px' : 'auto'};
  svg {
    color: ${({ theme, $mimeType }) => {
      switch ($mimeType) {
        case FolderManagerMimeTypes.PDF:
          return `${theme?.palette?.pdfIconColor} !important` || 'rgb(213,80,80) !important';
        case FolderManagerMimeTypes.FOLDER:
          return `${theme?.palette?.pdfIconColor} !important` || 'rgb(101, 101, 101) !important';
        case FolderManagerMimeTypes.PPTX:
        case FolderManagerMimeTypes.SLIDE:
          return `${theme?.palette?.pptxIconColor} !important` || 'rgb(91, 127, 245) !important';
        default:
          return `${theme?.palette?.defaultIconColor} !important` || 'rgb(91, 127, 245) !important';
      }
    }};
  }
`;

export const getIconForMimeType = (mimeType: string, isShared?: boolean, isSlideRepositoryFolder?: boolean) => {
  switch (mimeType) {
    case FolderManagerMimeTypes.JPEG:
    case FolderManagerMimeTypes.JPG:
    case FolderManagerMimeTypes.PNG:
      return (
        <StyledIconWrapper $mimeType={mimeType}>
          <ImageIcon />
        </StyledIconWrapper>
      );
    case FolderManagerMimeTypes.PDF:
      return (
        <StyledIconWrapper $mimeType={mimeType}>
          <PictureAsPdfIcon />
        </StyledIconWrapper>
      );
    case FolderManagerMimeTypes.FOLDER:
    case FolderManagerMimeTypes.WORKSPACE: {
      const folderImg = isShared ? (
        <img src={sharedFolderIcon} alt="Shared folder" />
      ) : (
        <img src={folderIcon} alt="Folder" />
      );

      if (isSlideRepositoryFolder) {
        return (
          <StyledIconWrapper $mimeType={mimeType}>
            <StyledFolderIconWrapper>
              {folderImg}
              <GridViewIcon className="slide-repo-badge" />
            </StyledFolderIconWrapper>
          </StyledIconWrapper>
        );
      }

      return (
        <StyledIconWrapper $mimeType={mimeType}>
          {folderImg}
        </StyledIconWrapper>
      );
    }
    case FolderManagerMimeTypes.PRESENTATION:
      return (
        <StyledIconWrapper $mimeType={mimeType}>
          <SlideshowIcon />
        </StyledIconWrapper>
      );
    case FolderManagerMimeTypes.SLIDE:
      return (
        <StyledIconWrapper $mimeType={mimeType}>
          <GridViewIcon />
        </StyledIconWrapper>
      );
    default:
      return (
        <StyledIconWrapper $mimeType={mimeType}>
          <ArticleIcon />
        </StyledIconWrapper>
      );
  }
};

export const handleAssignItemsToFolder = async (payload: IAssignItemsToFolderPayload) => {
  const {
    target: { id: targetId, name: targetName },
    selectedItems: itemsToMove,
    updateFolderCb,
    updateAssetCb,
    updatePresentationCb,
  } = payload || {};

  if (!targetId) {
    const message = 'ERROR: No target ID provided!.';
    console.error(message);
    return { errors: [message] };
  }

  if (!itemsToMove || !itemsToMove?.length) {
    const message = 'No selected items. Please make a selection before dragging.';
    console.error(message);
    return { errors: [message] };
  }

  // Only prevent moving a folder into itself (not presentations/assets with same ID)
  const movingFolderToItself = itemsToMove?.some(
    (item: FolderManagerItem) => item.type === FolderManagerItemTypes.FOLDER && item.id === targetId,
  );
  if (movingFolderToItself) {
    const message = 'Trying to move folder to itself, aborting';
    console.error(message);
    return { errors: [message] };
  }

  let resultObject: { message: string; errors: string[] } = {
    message: '',
    errors: [],
  };

  try {
    let assignToFolderPromises: Promise<any>[] = [];

    for (const item of itemsToMove) {
      if (!item?.type) {
        const message = `Error moving item with id: ${item.id} to the target folder. No item type!`;
        resultObject.errors.push(message);
        console.error(message);
        continue;
      }

      if (!item.id || Number.isNaN(item.id)) {
        const message = `Error moving item with id: ${item.id} to the target folder. No or invalid item ID!`;
        resultObject.errors.push(message);
        console.error(message);
        continue;
      }

      if (item.type === FolderManagerItemTypes.FOLDER) {
        assignToFolderPromises.push(updateFolderCb({ folderId: item.id, data: { parent_folder_id: targetId } }));
      } else if (item.type === FolderManagerItemTypes.PRESENTATION) {
        if (!updatePresentationCb) {
          const message = `Error moving presentation with id: ${item.id}. No update presentation callback provided!`;
          resultObject.errors.push(message);
          console.error(message);
          continue;
        }

        assignToFolderPromises.push(
          updatePresentationCb({
            presentationId: item.id,
            data: { folder_id: targetId },
            currentFolderId: item.parentId,
          }),
        );
      } else {
        assignToFolderPromises.push(updateAssetCb({ assetId: item.id, data: { folder_id: targetId } }));
      }
    }

    await Promise.all(assignToFolderPromises);

    resultObject.message = `Moved ${itemsToMove?.length} items to the folder "${targetName}"`;

    return resultObject;
  } catch (error: any) {
    resultObject.errors.push(`Error moving items to folder. ${error?.message ? error.message : ''}`);
    console.error('Error moving items to folder');
    return resultObject;
  }
};

export const getActionsForItemType = (
  itemType: FolderManagerItemTypes | null,
  filterByType?: FolderManagerItemActionTypes,
) => {
  let actions = [];

  switch (itemType) {
    case FolderManagerItemTypes.FOLDER:
      actions = [
        { name: FolderManagerItemActionNames.RENAME, type: FolderManagerItemActionTypes.SINGLE },
        { name: FolderManagerItemActionNames.MOVE, type: FolderManagerItemActionTypes.MULTI },
        { name: FolderManagerItemActionNames.SHARE, type: FolderManagerItemActionTypes.SINGLE },
        { name: FolderManagerItemActionNames.RESTORE, type: FolderManagerItemActionTypes.MULTI },
        { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
      ];
      break;
    case FolderManagerItemTypes.PRESENTATION:
      actions = [
        { name: FolderManagerItemActionNames.EDIT, type: FolderManagerItemActionTypes.SINGLE },
        { name: FolderManagerItemActionNames.DOWNLOAD, type: FolderManagerItemActionTypes.SINGLE },
        { name: FolderManagerItemActionNames.MOVE, type: FolderManagerItemActionTypes.MULTI },
        { name: FolderManagerItemActionNames.RESTORE, type: FolderManagerItemActionTypes.MULTI },
        { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
      ];
      break;
    default:
      actions = [
        { name: FolderManagerItemActionNames.RENAME, type: FolderManagerItemActionTypes.SINGLE },
        { name: FolderManagerItemActionNames.MOVE, type: FolderManagerItemActionTypes.MULTI },
        { name: FolderManagerItemActionNames.COPY, type: FolderManagerItemActionTypes.SINGLE },
        { name: FolderManagerItemActionNames.DOWNLOAD, type: FolderManagerItemActionTypes.MULTI },
        { name: FolderManagerItemActionNames.MANAGE_VERSIONS, type: FolderManagerItemActionTypes.SINGLE },
        { name: FolderManagerItemActionNames.RESTORE, type: FolderManagerItemActionTypes.MULTI },
        { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
      ];
  }

  return filterByType ? actions.filter((item) => item.type === filterByType) : actions;
};

export const getFolderManagerColumns = ({ onAction, selectedItems, isLoading }: ColumnOptions) => [
  {
    label: 'Name',
    field: 'name',
    customRender: (row: any) => (
      <StyledNameCell type={row?.type} className={classNames({ orphaned: row.sourceId && !row.source })}>
        {row?.status === FolderManagerStatusTypes.COMPLETED ? (
          <>
            {getIconForMimeType(row?.type, row?.isShared, row?.isSlideRepositoryFolder)}
            {row?.sourceId ? <ShortcutIcon type="list" /> : <></>}
          </>
        ) : (
          <CircularLoader />
        )}
        <span>{row?.displayName || row?.name || ''}</span>
      </StyledNameCell>
    ),
    sort: (a: any, b: any) => {
      const nameA = (a?.displayName || a?.name || '').toLowerCase();
      const nameB = (b?.displayName || b?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    },
  },
  {
    label: 'Size',
    field: 'size',
    width: '7%',
    customRender: (row: any) => (
      <div style={{ textAlign: 'center' }}>{row?.fileSize ? formatFileSize(row.fileSize) : '-'}</div>
    ),
    sort: (a: any, b: any) => {
      const sizeA = a?.fileSize || 0;
      const sizeB = b?.fileSize || 0;
      return sizeA - sizeB;
    },
  },
  {
    label: 'Owner',
    field: 'owner',
    width: '15%',
    customRender: (row: any) => {
      const firstName = row?.owner?.FirstName || '';
      const lastName = row?.owner?.LastName || '';
      return [firstName, lastName].filter(Boolean).join(' ') || null;
    },
    sort: (a: any, b: any) => {
      const fullNameA = `${a?.owner?.FirstName || ''} ${a?.owner?.LastName || ''}`.trim().toLowerCase();
      const fullNameB = `${b?.owner?.FirstName || ''} ${b?.owner?.LastName || ''}`.trim().toLowerCase();
      return fullNameA.localeCompare(fullNameB);
    },
  },
  {
    label: 'Last Modified',
    field: 'updatedAt',
    width: '15%',
    customRender: (row: any) => formatDateTime(row?.updatedAt),
    sort: (a: any, b: any) => {
      const dateA = new Date(a?.updatedAt || 0).getTime();
      const dateB = new Date(b?.updatedAt || 0).getTime();
      return dateA - dateB;
    },
  },
  {
    label: '',
    field: 'action',
    width: '10%',
    customRender: (row: any) =>
      !row.isParentLink && (
        <FolderManagerItemActions
          item={row}
          isLoading={isLoading}
          selectedItems={selectedItems}
          onAction={onAction}
          isTableView
        />
      ),
    // No sort function for action column - sorting disabled
  },
];
